import API_BASE_URL from '../config/apiConfig';
import { apiClient } from './authService';

/**
 * Get current agent assignments (agent dashboard).
 * Endpoint: GET /api/agent/assignments?page=0&size=20
 * @param {Object} params
 * @param {number} params.page
 * @param {number} params.size
 * @returns {Promise<Object>}
 */
export const getMyAgentAssignments = async ({ page = 0, size = 20 } = {}) => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    const response = await apiClient(
      `${API_BASE_URL}/api/agent/assignments?${queryParams.toString()}`,
      { method: 'GET' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch assignments';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get agent assignments error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get pending invitations for the current agent.
 * Endpoint: GET /api/agent/assignments/pending
 * @returns {Promise<Object>}
 */
export const getPendingAgentAssignments = async () => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/agent/assignments/pending`,
      { method: 'GET' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch pending invitations';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get pending invitations error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Accept an assignment invitation.
 * Endpoint: POST /api/agent/assignments/{assignmentId}/accept
 * @param {string} assignmentId
 * @returns {Promise<Object>}
 */
export const acceptAgentAssignment = async (assignmentId) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/agent/assignments/${assignmentId}/accept`,
      { method: 'POST' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to accept invitation';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Accept invitation error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Reject an assignment invitation.
 * Endpoint: POST /api/agent/assignments/{assignmentId}/reject
 * @param {string} assignmentId
 * @returns {Promise<Object>}
 */
export const rejectAgentAssignment = async (assignmentId) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/agent/assignments/${assignmentId}/reject`,
      { method: 'POST' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to decline invitation';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Reject invitation error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

